import { useLayoutEffect, useRef, useState, useSyncExternalStore } from 'react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { API } from '@/config/constants';
import { getOrder, updateOrder } from '@/api/orders';
import { addressReviewFrom, addressAcceptance } from '@/utils/addressConfirmation';
import { isDesktopOffline } from '@/api/desktopSync';
import { getActiveSessionRevision, getActiveSessionType, getAuthTokenForRequest,
  subscribeActiveSessionRevision } from '@/api/authenticatedClient';
import { formatPhoneNumberDisplay } from '@/utils/phoneUtils';

export const ORDER_REVIEW_WAIT_MS = 15000;
const principal = binding => binding?.user?.id && binding?.user?.tenant_id
  ? JSON.stringify([binding.user.tenant_id, binding.user.id, binding.source]) : null;
const connection = () => JSON.stringify([window.lvbDesktop?.backendUrl || null, window.lvbDesktop?.localBackendUrl || null]);
const validOrder = (value, id) => value && typeof value === 'object' && !Array.isArray(value) && value.id === id;
const UNKNOWN = 'Save result not confirmed. The request may already have succeeded. Check the saved record before deciding whether to try again.';
const REFRESH_FAILED = 'Saved, but the display could not refresh. Retry refresh only; do not repeat the saved action.';

// One mounted Review opening owns its corrections and all asynchronous work.
// Aborting/retiring stops publication, never claims to cancel server writes.
export function useOrderReview({ order, fieldKeys = [], openingId, onSaved, onClose }) {
  const { sessionBinding } = useAuth() || {};
  const revision = useSyncExternalStore(subscribeActiveSessionRevision, getActiveSessionRevision);
  const [opening] = useState(() => {
    const fields = Object.fromEntries(fieldKeys.map(key => [key, key === 'recipient_phone'
      ? formatPhoneNumberDisplay(order?.[key]) || '' : order?.[key] ?? '']));
    const endpoint = `${API}/orders/${order?.id}`;
    return { order: { ...order }, openingId, principal: principal(sessionBinding), revision: sessionBinding?.revision,
      source: sessionBinding?.source, endpoint, token: API !== null ? getAuthTokenForRequest(endpoint) : null,
      connection: connection(), initial: fields, initialReviewed: !!order?.reviewed_by,
      fields, reviewed: !!order?.reviewed_by, active: false, retired: false, operation: null,
      unknown: false, refreshFailed: false, message: '', evidence: null, confirmation: null };
  });
  const latest = useRef(null);
  latest.current = { orderId: order?.id, openingId, binding: sessionBinding, onSaved, onClose };
  const [, render] = useState(0);
  const [, setOnlineRevision] = useState(0);
  const identityCurrent = () => !opening.retired && !!opening.principal
    && opening.principal === principal(latest.current.binding)
    && opening.revision === latest.current.binding?.revision && opening.revision === getActiveSessionRevision()
    && opening.source === getActiveSessionType() && latest.current.orderId === opening.order.id
    && latest.current.openingId === opening.openingId
    && (API !== null ? getAuthTokenForRequest(opening.endpoint) : null) === opening.token;
  const owns = () => opening.active && identityCurrent();
  const publish = () => { if (owns()) render(value => value + 1); };
  const online = () => navigator.onLine !== false && !isDesktopOffline();
  const supported = () => opening.source === 'cloud' && !!opening.token && online()
    && opening.connection === connection() && getAuthTokenForRequest(opening.endpoint) === opening.token;
  useLayoutEffect(() => {
    opening.active = true;
    render(value => value + 1);
    const connectionChanged = () => setOnlineRevision(value => value + 1);
    window.addEventListener('online', connectionChanged);
    window.addEventListener('offline', connectionChanged);
    return () => {
      opening.active = false;
      opening.operation?.controller.abort();
      window.removeEventListener('online', connectionChanged);
      window.removeEventListener('offline', connectionChanged);
    };
  }, [opening]);
  const visible = identityCurrent() && opening.revision === revision;
  const connectionCurrent = opening.connection === connection();
  useLayoutEffect(() => {
    if (!visible) { opening.retired = true; opening.operation?.controller.abort(); }
  }, [opening, visible]);
  useLayoutEffect(() => {
    if (!connectionCurrent) opening.operation?.controller.abort();
  }, [opening, connectionCurrent]);

  const bounded = async (label, execute) => {
    const attempt = { controller: new AbortController(), active: true, label };
    opening.operation = attempt;
    const current = () => owns() && opening.connection === connection() && attempt.active
      && opening.operation === attempt && !attempt.controller.signal.aborted;
    const config = { headers: { Authorization: `Bearer ${opening.token}` },
      signal: attempt.controller.signal, timeout: ORDER_REVIEW_WAIT_MS, suppressServerErrorNotification: true };
    let timer; let abort;
    publish();
    try {
      const stopped = new Promise((_, reject) => {
        abort = () => reject(new Error('Review operation stopped'));
        attempt.controller.signal.addEventListener('abort', abort, { once: true });
        timer = setTimeout(() => { reject(new Error('Review operation timed out')); attempt.controller.abort(); }, ORDER_REVIEW_WAIT_MS);
      });
      const value = await Promise.race([execute({ config, owns: current }), stopped]);
      if (!current()) throw new Error('Review operation no longer owns this connection');
      return value;
    } finally {
      attempt.active = false;
      clearTimeout(timer);
      if (abort) attempt.controller.signal.removeEventListener('abort', abort);
      if (opening.operation === attempt) opening.operation = null;
      publish();
    }
  };
  const close = () => {
    if (!owns()) return;
    opening.retired = true;
    opening.operation?.controller.abort();
    latest.current.onClose?.();
    render(value => value + 1);
  };
  const refresh = async () => {
    if (!owns() || opening.operation || !opening.refreshFailed) return;
    if (!supported()) { opening.message = `${REFRESH_FAILED} Reconnect to refresh.`; publish(); return; }
    try {
      await bounded('Refreshing saved changes', async context => {
        const value = latest.current.onSaved
          ? await latest.current.onSaved(opening.order.id, context)
          : await getOrder(opening.order.id, context.config);
        if (!context.owns()) throw new Error('Review refresh expired');
        if (!validOrder(value, opening.order.id)) throw new Error('Invalid saved order response');
        return value;
      });
      if (!owns()) return;
      opening.refreshFailed = false;
      toast.success('Review saved');
      close();
    } catch { if (owns()) { opening.message = REFRESH_FAILED; publish(); } }
  };
  const persist = async (snapshot, review = null) => {
    if (!owns() || opening.operation || opening.unknown || opening.refreshFailed) return;
    if (!supported()) { opening.message = 'Saving this review is unavailable offline. Reconnect using the original account; your corrections remain here.'; publish(); return; }
    const acceptance = review ? addressAcceptance(review) : null;
    if (review && !acceptance) return;
    opening.confirmation = null; opening.message = ''; opening.evidence = null;
    try {
      const result = await bounded('Saving review', context => updateOrder(opening.order.id,
        { ...snapshot, ...acceptance }, context.config));
      if (!owns()) return;
      if (!validOrder(result, opening.order.id)) throw new Error('Save acknowledgment did not identify this order');
    } catch (error) {
      if (!owns()) return;
      const addressReview = addressReviewFrom(error);
      if (addressReview) {
        opening.confirmation = { ...addressReview, snapshot };
      } else {
        const status = error?.response?.status;
        if (!status || status === 408 || status >= 500) {
          opening.unknown = true; opening.message = UNKNOWN;
        } else {
          const detail = error?.response?.data?.detail;
          opening.message = typeof detail === 'string' ? detail : 'Review was not saved. Check your corrections and try again.';
        }
      }
      publish(); return;
    }
    opening.refreshFailed = true; opening.message = REFRESH_FAILED;
    await refresh();
  };
  const canEdit = () => owns() && !opening.operation && !opening.unknown && !opening.refreshFailed && !opening.confirmation;
  const dirty = JSON.stringify(opening.fields) !== JSON.stringify(opening.initial) || opening.reviewed !== opening.initialReviewed;
  const checkSavedRecord = async () => {
    if (!owns() || opening.operation || !opening.unknown) return;
    if (!supported()) { opening.message = `${UNKNOWN} Reconnect to check the saved record.`; publish(); return; }
    opening.evidence = null;
    try {
      const value = await bounded('Checking saved record', context => getOrder(opening.order.id, context.config));
      if (!owns()) return;
      if (!validOrder(value, opening.order.id)) throw new Error('Invalid saved order response');
      opening.evidence = value;
      opening.message = `${UNKNOWN} This is a current observation, not proof that an earlier request has stopped. Your corrections have not been replaced.`;
    } catch { if (owns()) opening.message = `${UNKNOWN} The saved record could not be checked. Try checking again when connected.`; }
    publish();
  };
  return {
    order: visible ? opening.order : null, formData: visible ? opening.fields : {}, reviewed: visible && opening.reviewed,
    setField: (key, value) => { if (canEdit() && Object.hasOwn(opening.initial, key)) { opening.fields = { ...opening.fields, [key]: value }; publish(); } },
    setReviewed: value => { if (canEdit()) { opening.reviewed = value === true; publish(); } },
    visible, owns, dirty: visible && dirty, pending: visible ? opening.operation?.label || null : null,
    disabled: !visible || !canEdit(), canEdit: visible && canEdit(),
    canSave: visible && canEdit() && supported(),
    message: visible ? opening.message || (!supported() ? 'Saving this review is unavailable offline. Your corrections remain here until you reconnect using the original account.' : '') : '',
    unknown: visible && opening.unknown, refreshFailed: visible && opening.refreshFailed,
    evidence: visible ? opening.evidence : null, addressConfirmation: visible ? opening.confirmation?.difference || null : null,
    addressReview: visible ? opening.confirmation : null,
    save: () => { if (canEdit()) return persist({ ...opening.fields, accuracy_reviewed: opening.reviewed }); },
    retryRefresh: refresh, checkSavedRecord,
    allowRetry: () => {
      if (!owns() || opening.operation || !opening.unknown || !opening.evidence) return;
      if (!window.confirm('Have you compared the saved record with your corrections? An earlier save may still finish. Enable editing and a possible new save only after reviewing that risk.')) return;
      opening.unknown = false; opening.evidence = null; opening.message = 'Corrections retained. Review them before explicitly saving again.'; publish();
    },
    acceptAddress: () => { if (opening.confirmation) return persist(opening.confirmation.snapshot, opening.confirmation); },
    editAddress: () => { if (owns() && !opening.operation) { opening.confirmation = null; publish(); } },
    requestClose: () => {
      if (!owns()) return;
      const warning = opening.operation || opening.unknown
        ? 'A save or check is outstanding, or its result is uncertain. Leaving does not cancel server work. Leave this review and discard its in-memory corrections?'
        : opening.refreshFailed ? 'Your review was saved, but the display has not refreshed. Leave this review?'
          : dirty ? 'Discard your unsaved review corrections?' : null;
      if (!warning || window.confirm(warning)) close();
    },
  };
}
